// 📁 src/pages/MentorPending.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Stack,
  Card,
  CardContent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "../utils/axiosInstance";
import { motion } from "framer-motion";

const skillSuggestions = [
  "SaaS", "B2B", "B2C", "Fundraising", "Pitching",
  "Go-to-Market", "Product Strategy", "Sales", "Marketing",
  "UI/UX", "Finance", "Operations"
];

const industrySuggestions = [
  "EdTech", "FinTech", "HealthTech", "AgriTech", "FoodTech",
  "E-commerce", "DeepTech", "AI/ML", "Marketplace"
];

const languageSuggestions = ["English", "Hindi", "Tamil", "Telugu", "Marathi", "Bengali"];

const MentorPending = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Form state
  const [bio, setBio] = useState("");
  const [skills, setSkills] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [hourlyRate, setHourlyRate] = useState("");
  const [meetingLink, setMeetingLink] = useState("");

  // 🧠 AI bio assistant
  const generateAIBio = async () => {
    try {
      const { data } = await axios.post("/api/mentors/ai/bio", {
        skills,
        industries,
      });
      setBio(data.bio);
    } catch (error) {
      console.error("❌ AI bio generation failed", error);
    }
  };

  const toggleArrayValue = (value, setter, list) => {
    setter(list.includes(value) ? list.filter(v => v !== value) : [...list, value]);
  };

  const saveMentorProfile = async () => {
    if (!bio || skills.length === 0 || industries.length === 0 || !hourlyRate)
      return alert("⚠ Please complete all required fields");

    setLoading(true);
    try {
      await axios.put("/api/mentors/profile", {
        bio,
        skills,
        industries,
        languages,
        hourlyRate,
        meetingLink,
      });

      alert("🎯 Profile submitted! Your application is under review.");
    } catch (err) {
      console.error("❌ Error saving mentor profile:", err);
      alert("Error saving profile");
    }
    setLoading(false);
  };

  // 🔄 Auto redirect after approval (real-time)
  useEffect(() => {
    const eventSource = new EventSource(`/api/mentors/status-stream`, {
      withCredentials: true,
    });

    eventSource.onmessage = (e) => {
      const { status } = JSON.parse(e.data);
      if (status === "approved") navigate("/mentor-dashboard");
    };

    return () => eventSource.close();
  }, [navigate]);

  return (
    <Box sx={{ maxWidth: 850, mx: "auto", mt: 6, mb: 8 }}>
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
          ⏳ Mentor Application Under Review
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Complete your mentor profile to speed up approval & increase bookings.
        </Typography>
      </motion.div>

      {/* 🔥 Mentor Onboarding Form */}
      <Card sx={{ p: 2 }}>
        <CardContent>
          <Typography fontWeight={600} sx={{ mb: 1 }}>👤 Mentor Bio *</Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Tell founders about your background & expertise"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button onClick={generateAIBio} variant="outlined" sx={{ mb: 3 }}>
            🤖 Generate Bio with AI
          </Button>

          <Typography fontWeight={600}>🔥 Skills *</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
            {skillSuggestions.map((s) => (
              <Chip
                key={s}
                label={s}
                clickable
                color={skills.includes(s) ? "primary" : "default"}
                onClick={() => toggleArrayValue(s, setSkills, skills)}
              />
            ))}
          </Stack>

          <Typography fontWeight={600}>🏭 Industries *</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
            {industrySuggestions.map((s) => (
              <Chip
                key={s}
                label={s}
                clickable
                color={industries.includes(s) ? "primary" : "default"}
                onClick={() => toggleArrayValue(s, setIndustries, industries)}
              />
            ))}
          </Stack>

          <Typography fontWeight={600}>🗣 Languages (optional)</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
            {languageSuggestions.map((s) => (
              <Chip
                key={s}
                label={s}
                clickable
                color={languages.includes(s) ? "primary" : "default"}
                onClick={() => toggleArrayValue(s, setLanguages, languages)}
              />
            ))}
          </Stack>

          <TextField
            fullWidth
            label="Hourly Rate (INR) *"
            type="number"
            value={hourlyRate}
            onChange={(e) => setHourlyRate(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Default Meeting Link (Google Meet/Zoom)"
            placeholder="https://meet.google.com/your-room"
            value={meetingLink}
            onChange={(e) => setMeetingLink(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={saveMentorProfile}
            disabled={loading}
          >
            {loading ? "Saving..." : "💾 Submit Profile for Approval"}
          </Button>
        </CardContent>
      </Card>

      <Typography color="text.secondary" sx={{ mt: 4, textAlign: "center" }}>
        Admin reviews happen daily — you’ll receive an email & auto-redirect when approved.
      </Typography>
    </Box>
  );
};

export default MentorPending;
